"use client";

import React from "react";
import { Instagram, Linkedin, Compass, Mail, ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { Separator } from "@/components/ui/separator";
function RecursiveFooterNav({
  items
}: {
  items: MenuItem[];
}) {
  if (!items || items.length === 0) return null;
  return <ul className="flex flex-col space-y-2.5">
      {items.map((item, i) => <li key={item.id || i} data-index={i} className="flex flex-col space-y-1">
          <Link to={item.href || "#"} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="pl-3 border-l border-border/60 my-1">
              <RecursiveFooterNav items={item.children} />
            </div>}
        </li>)}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col space-y-2">
        <div className="h-3 w-20 bg-muted/60 animate-pulse" />
        <div className="h-3 w-16 bg-muted/60 animate-pulse" />
        <div className="h-3 w-24 bg-muted/60 animate-pulse" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="flex flex-col space-y-2.5">
        <li data-index={0}>
          <Link to="/HomePage" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
            Home
          </Link>
        </li>
        <li data-index={1}>
          <Link to="#" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200">
            Studio
          </Link>
        </li>
      </ul>;
  }
  return <RecursiveFooterNav items={menuItems} />;
}
export default function Footer() {
  const prefersReducedMotion = useReducedMotion();
  const currentYear = new Date().getFullYear();
  const socials = [{
    name: "Instagram",
    href: "https://instagram.com/sldesigninteriors",
    icon: Instagram
  }, {
    name: "LinkedIn",
    href: "https://linkedin.com",
    icon: Linkedin
  }];
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-12">
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1]
      }} className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 pb-14">
          {/* Brand Column */}
          <div className="md:col-span-4 lg:col-span-5 flex flex-col justify-between space-y-6">
            <div>
              <Link to="/" className="inline-block focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                <WvcLogo className="h-7 w-auto" />
              </Link>
              <p className="mt-4 text-xs tracking-wider uppercase text-muted-foreground max-w-sm leading-relaxed">
                Bespoke residential spaces and architecture grounded in understated elegance and proportion.
              </p>
            </div>

            <div className="pt-2">
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground block mb-2 font-medium">
                Inquiries
              </span>
              <a href="mailto:contact@slrealtyanddesign.com" className="inline-flex items-center text-sm text-foreground hover:text-muted-foreground transition-colors group">
                <span>contact@slrealtyanddesign.com</span>
                <ArrowUpRight className="ml-1 size-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </a>
            </div>
          </div>

          {/* Navigation Menu Column */}
          <div className="md:col-span-3 lg:col-span-3">
            <h3 className="text-xs uppercase tracking-widest text-foreground font-semibold mb-4">
              Navigation
            </h3>
            <MenuProvider menu_id={16} className="footer-menu">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Studio Location & Hours */}
          <div className="md:col-span-3 lg:col-span-2">
            <h3 className="text-xs uppercase tracking-widest text-foreground font-semibold mb-4">
              Studio
            </h3>
            <address className="not-italic text-xs text-muted-foreground uppercase tracking-widest space-y-2 leading-relaxed">
              <p></p>
              <p><br /></p>
              <p></p>
              <p className="pt-2 text-[11px] text-muted-foreground/80 normal-case tracking-normal">
                By appointment only
              </p>
            </address>
          </div>

          {/* Social Links */}
          <div className="md:col-span-2 lg:col-span-2">
            <h3 className="text-xs uppercase tracking-widest text-foreground font-semibold mb-4">
              Connect
            </h3>
            <ul className="flex flex-col space-y-2.5">
              {socials.map((social, i) => {
              const IconComponent = social.icon;
              return <li key={social.name} data-index={i}>
                    <Link to={social.href} newTab className="inline-flex items-center text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200 group">
                      <IconComponent className="size-3.5 mr-2 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span>{social.name}</span>
                    </Link>
                  </li>;
            })}
            </ul>
          </div>
        </motion.div>

        <Separator className="bg-border/70" />

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] uppercase tracking-widest text-muted-foreground">
          <p>© 2026 S & L DESIGN. ALL RIGHTS RESERVED.</p>
          <div className="flex items-center space-x-6">
            <Link to="#" className="hover:text-foreground transition-colors">
              Privacy &amp; Terms
            </Link>
            
          </div>
        </div>
      </div>
    </section>;
}